import React, { useState } from "react";
import { FiCamera } from "react-icons/fi";
import { useTranslation } from "react-i18next";

// ✅ Fonction intégrée directement dans le fichier
function getImageWithFallback(imagePath, fallback) {
  if (!imagePath) return fallback;
  if (typeof imagePath !== 'string') return fallback;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  if (imagePath.startsWith('/')) {
    return imagePath;
  }
  return fallback;
}

const ArtworkImages = ({ images = [], onShowImageUpload, onSetPrimaryImage, onRemoveImage, readOnly = false }) => {
  const { t } = useTranslation();
  const fallbackPath = "/images/artworks/Image_principale_Image_Not_Available.png";
  
  // ✅ Normalise: accepte strings OU objets
  const normalizedImages = (Array.isArray(images) ? images : [])
    .filter(Boolean)
    .map((img, idx) => {
      if (typeof img === "string") {
        return { id: `img_${idx}`, url: img, path: img, isPrimary: idx === 0 };
      }
      return { id: img.id ?? `img_${idx}`, ...img };
    });

  const defaultPrimaryImage = normalizedImages.find(img => img.isPrimary) || normalizedImages[0];
  const primaryImage = normalizedImages.find(img => img.isPrimary) || normalizedImages[0];
  const [selectedImage, setSelectedImage] = useState(defaultPrimaryImage);
  const [fadeKey, setFadeKey] = useState(0);
  const [aspectClass, setAspectClass] = useState('aspect-video');

  const getAspectRatioClass = (ratio) => {
    if (Math.abs(ratio - 1) < 0.1) return 'aspect-square';
    if (ratio > 1) return 'aspect-video';
    return 'aspect-[3/4]';
  };

  if (readOnly) {
    const imagePath = selectedImage?.url || selectedImage?.path || fallbackPath;

    return (
      <div className="w-full max-w-5xl mx-auto flex flex-col items-center">
        {/* Image principale affichée */}
        <div className={`w-full max-h-[600px] ${aspectClass} bg-black flex items-center justify-center rounded-md overflow-hidden transition-all duration-500`}>
          <img
            key={fadeKey}
            src={getImageWithFallback(imagePath, fallbackPath)}
            alt={t("artworkImages.alt.main")}
            className="max-h-full max-w-full object-contain transition-opacity duration-500 ease-in-out opacity-0 animate-fade-in"
            onLoad={(e) => {
              e.target.classList.remove("opacity-0");
              const { naturalWidth, naturalHeight } = e.target;
              if (naturalWidth && naturalHeight) {
                const ratio = naturalWidth / naturalHeight;
                setAspectClass(getAspectRatioClass(ratio));
              }
            }}
            onError={(e) => {
              e.target.src = fallbackPath;
              e.target.classList.remove("opacity-0");
            }}
          />
        </div>

        {/* Miniatures cliquables */}
        {normalizedImages.length > 0 && (
          <div className="mt-6 grid grid-cols-5 gap-2 w-full">
            {normalizedImages.map((image) => (
              <div 
                key={image.id} 
                className="relative cursor-pointer" 
                onClick={() => {
                  setFadeKey(prev => prev + 1);
                  setSelectedImage(image);
                }}
              >
                <img
                  src={getImageWithFallback(image.url || image.path, '/images/thumbnails/Miniature_Image_Not_Available.png')}
                  alt={t("artworkImages.alt.thumbnail", { id: image.id })}
                  className="w-full h-20 object-contain rounded"
                  onError={(e) => { e.target.src = '/images/thumbnails/Miniature_Image_Not_Available.png'; }}
                />
                {image.id === selectedImage?.id && (
                  <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">
                    {t("artworkImages.selected")}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Image principale en mode édition */}
      <div
        className={`relative group border-2 border-dashed border-gray-300 rounded-md w-full cursor-pointer ${aspectClass} transition-all duration-500`}
        onClick={onShowImageUpload}
      >
        {primaryImage ? (
          <img
            src={getImageWithFallback(primaryImage.path, "../../images/artworks/Image_principale_Image_Not_Available.png")}
            alt={t("artworkImages.alt.primary")}
            className="w-full h-full object-contain rounded-md"
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.target;
              if (naturalWidth && naturalHeight) {
                const ratio = naturalWidth / naturalHeight;
                setAspectClass(getAspectRatioClass(ratio));
              }
            }}
            onError={(e) => { e.target.src = fallbackPath; }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <FiCamera className="h-8 w-8" />
            <span className="text-sm mt-2">{t("artworkImages.addImage")}</span>
          </div>
        )}
      </div>

      {/* Miniatures en édition */}
      {normalizedImages.length > 0 && (
        <>
          <div className="mt-6 grid grid-cols-5 gap-2">
            {normalizedImages.map((image) => (
              <div
                key={image.id}
                className={`relative group cursor-pointer ${image.isPrimary ? 'ring-2 ring-blue-500' : ''}`}
              >
                <img
                  src={getImageWithFallback(image.path, '/images/thumbnails/Miniature_Image_Not_Available.png')}
                  alt={t("artworkImages.alt.view", { id: image.id })}
                  className="w-full h-20 object-contain rounded"
                  onClick={() => onSetPrimaryImage?.(image.id)}
                  onError={(e) => { e.target.src = '/images/thumbnails/Miniature_Image_Not_Available.png'; }}
                />

                {image.isPrimary && (
                  <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded">
                    {t("artworkImages.primary")}
                  </span>
                )}

                {!image.isPrimary && (
                  <button
                    type="button"
                    onClick={() => {
                      const shouldRemove = window.confirm(t("artworkImages.deleteConfirm"));
                      if (shouldRemove) {
                        onRemoveImage?.(image.id);
                      }
                    }}
                    className="absolute top-1 right-1 bg-red-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center opacity-80 hover:opacity-100"
                    title={t("artworkImages.deleteTitle")}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {normalizedImages.length < 10 && (
              <button
                onClick={onShowImageUpload}
                className="h-20 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-500 hover:border-gray-400 rounded"
              >
                <FiCamera className="w-6 h-6" />
              </button>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-4 text-center">
            {t("artworkImages.formats")}<br />
            {t("artworkImages.limit")}
          </p>
        </>
      )}
    </>
  );
};

export default ArtworkImages;